import { getFingerprint } from './fingerprint-utility';
import { performWrappedFetch } from './wrapped-fetch';
import { CredentialsResponse, SessionHandle } from './types';

const GHSDK_DATA_KEY = '__ghsdk_data';

interface AnonymousAuthObject {
  client_id: string;
  device_id: number;
  brand: string;
  scope: string;
}

interface MinimalAuthData {
  auth: {
    credentials: {
      session_handle: SessionHandle;
      credential: {
        ud_id?: string;
      };
    };
  };
}

/**
 * Extracts minimal auth data for localStorage storage
 * Takes entire credentials object and extracts session_handle and ud_id
 * Stores entire session_handle and ud_id
 * Maintains nested structure for backward compatibility
 */
function extractMinimalAuthData(
  credentials: CredentialsResponse,
): MinimalAuthData {
  return {
    auth: {
      credentials: {
        session_handle: credentials.session_handle,
        credential: {
          ud_id: credentials.credential?.ud_id,
        },
      },
    },
  };
}

async function performAnonAuth(
  httpServiceData: HttpServiceData,
): Promise<string> {
  const { apiUrl, brand, clientId }: HttpServiceData = httpServiceData;

  const ANONYMOUS_AUTH_OBJECT: AnonymousAuthObject = {
    client_id: clientId,
    device_id: getFingerprint(),
    brand,
    scope: 'anonymous',
  };

  const credentials: CredentialsResponse = await performWrappedFetch({
    method: 'POST',
    url: apiUrl + '/auth/anon',
    data: ANONYMOUS_AUTH_OBJECT,
  });

  const minimalAuthData = extractMinimalAuthData(credentials);

  localStorage.setItem(GHSDK_DATA_KEY, JSON.stringify(minimalAuthData));

  return credentials.session_handle.access_token;
}

interface HttpServiceData {
  apiUrl: string;
  authToken?: string;
  brand: string;
  clientId: string;
}

interface DesiredExecutionProps {
  httpServiceData: HttpServiceData;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  url: string;
  data?: any;
  useCookieAuth?: boolean;
}

/**
 * Performs an authenticated fetch request with bearer token
 */
function performAuthedFetch(
  desiredExecutionProps: DesiredExecutionProps,
): Promise<any> {
  const { authToken }: HttpServiceData = desiredExecutionProps.httpServiceData;
  return performWrappedFetch({
    method: desiredExecutionProps.method,
    useCookieAuth: desiredExecutionProps.useCookieAuth,
    url: desiredExecutionProps.url,
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    data: desiredExecutionProps.data,
  });
}

/**
 * Handles authenticated execution with token refresh if needed
 * Skips anonymous auth if cookie authentication is supported
 */
async function handleChainedAuthedExecution(
  desiredExecutionProps: DesiredExecutionProps,
): Promise<any> {
  // If using cookie auth, skip token refresh
  if (desiredExecutionProps.useCookieAuth) {
    return performAuthedFetch(desiredExecutionProps);
  }

  // Otherwise, refresh the token and perform the request
  const freshAuthToken: string = await performAnonAuth(
    desiredExecutionProps.httpServiceData,
  );

  return performAuthedFetch({
    ...desiredExecutionProps,
    httpServiceData: {
      ...desiredExecutionProps.httpServiceData,
      authToken: freshAuthToken,
    },
  });
}

enum ERROR_STATUS {
  UNAUTHORIZED = 401,
}

/**
 * Retrieves the current auth token from localStorage
 * Reads from nested path: auth.credentials.session_handle.access_token
 * This only contains the token (no PII data)
 * @returns The access token or undefined if not found
 */
function getAuthToken(): string | undefined {
  try {
    const stored = localStorage.getItem(GHSDK_DATA_KEY);
    if (!stored) {
      return undefined;
    }
    const parsed = JSON.parse(stored);
    return parsed?.auth?.credentials?.session_handle?.access_token;
  } catch {
    return undefined;
  }
}

/**
 * Main entry point for executing native fetch requests
 * Handles authentication flow based on cookie support and token availability
 */
async function handleExecuteNativeFetch(
  desiredExecutionProps: DesiredExecutionProps,
): Promise<any> {
  const { authToken }: HttpServiceData = desiredExecutionProps.httpServiceData;

  // If using cookie auth, skip token-based auth entirely
  if (desiredExecutionProps.useCookieAuth) {
    return handleChainedAuthedExecution(desiredExecutionProps);
  }

  // Otherwise, try token-based auth if token exists
  if (authToken) {
    try {
      return await performAuthedFetch(desiredExecutionProps);
    } catch (error) {
      if (ERROR_STATUS.UNAUTHORIZED !== (error as Response).status) {
        return Promise.reject(error);
      }
    }
  }

  return handleChainedAuthedExecution(desiredExecutionProps);
}

export {
  handleExecuteNativeFetch,
  DesiredExecutionProps,
  GHSDK_DATA_KEY,
  getAuthToken,
  extractMinimalAuthData,
};
